import React, { useState, useEffect } from 'react'
import { SPORT_HIERARCHY } from '../config/sports'
import { settlementFields } from '../TRACKER_PAGE/settlement'
import { subjectFor } from '../TRACKER_PAGE/LiveBetMeter'
import { playerImageUrl, teamLogoUrl } from '../config/playerImages'

/* ── Selection subject image ───────────────────────────────────────────────────
   subjectFor() (tracker) decides WHICH image a market gets; this decides what to
   draw when that image does not exist, which is most rows for most sports:
   headshots are only uploaded for five leagues, and crests only exist for team
   sports, so tennis, a Challenger player, or any league without a folder would
   otherwise leave a blank box.

   A blank box reads as a loading failure. A glyph reads as "no photo", which is
   the truth. Falls back in two steps:
     player with no photo -> a person mark, inside the same ringed disc
     anything else        -> the SPORT's own mark, drawn bare like a crest
*/

// League -> sport group, inverted from SPORT_HIERARCHY so a new league added
// there is covered here automatically.
export const SPORT_GROUP = Object.entries(SPORT_HIERARCHY).reduce((m, [group, { leagues }]) => {
  leagues.forEach(l => { m[l] = group })
  return m
}, {})

const G = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' }

// One mark per sport group. Deliberately simple line marks: at 18px anything
// with interior detail turns to mush.
export const SPORT_GLYPHS = {
  // Real tennis-ball seam: two arcs whose ENDS sit on the ball (the 45 degree
  // points of r=9) and which bow toward the centre, left one to the right and
  // right one to the left. The old version bowed them outward from the edges,
  // which is the one thing a tennis ball's seam never does.
  'Tennis': <><circle cx="12" cy="12" r="9" {...G} /><path d="M5.6 5.6a9 9 0 0 1 0 12.8" {...G} /><path d="M18.4 5.6a9 9 0 0 0 0 12.8" {...G} /></>,
  'Basketball': <><circle cx="12" cy="12" r="9" {...G} /><path d="M3 12h18M12 3v18M5.6 5.6a12 12 0 0 0 12.8 12.8M18.4 5.6A12 12 0 0 1 5.6 18.4" {...G} /></>,
  // Bat and ball, NOT a stitched ball. A ball with curved seams is the same
  // primitive as the tennis one — circle plus two arcs — and at this size the
  // only thing separating them is how deep the arcs bow, which is not a
  // difference you can read in a table. Changing the FORM is what makes it
  // legible: the bat's diagonal is recognisable before the shapes resolve.
  'Baseball': <><circle cx="6.6" cy="6.6" r="3.1" {...G} /><path d="M18.6 5.4 11.2 12.8" {...G} strokeWidth="3" /><path d="M11.2 12.8 7.6 16.4" {...G} /><circle cx="6.4" cy="17.6" r="1.3" {...G} /></>,
  'Soccer': <><circle cx="12" cy="12" r="9" {...G} /><path d="m12 7.5 3.6 2.6-1.4 4.2H9.8L8.4 10.1z" {...G} /><path d="M12 3v4.5M4.2 9.6l4.2.5M19.8 9.6l-4.2.5M7.4 19.6l2.4-5.3M16.6 19.6l-2.4-5.3" {...G} /></>,
  'Hockey': <><ellipse cx="8" cy="17" rx="4.5" ry="2.2" {...G} /><path d="M14 4.5 16.5 15M16.5 15c2.5.4 4 1.2 4 2.2" {...G} /></>,
  'American Football': <><path d="M4 20c-1-6 9-17 16-16 1 6-9 17-16 16Z" {...G} /><path d="M9.5 14.5 14.5 9.5M10.8 12.2l1.4 1.4M12.2 10.8l1.4 1.4" {...G} /></>,
  'Aussie Rules': <><ellipse cx="12" cy="12" rx="5.5" ry="9" {...G} /><path d="M12 4.2v15.6M9.6 8h4.8M9.6 16h4.8" {...G} /></>,
  'Rugby League': <><ellipse cx="12" cy="12" rx="9" ry="5.5" transform="rotate(-35 12 12)" {...G} /><path d="m8.5 15.5 7-7M10.4 13.6l1.2 1.2M13.6 10.4l1.2 1.2" {...G} /></>,
  // Boxing glove: a rounded fist, the thumb bump on its left and a cuff below.
  // The cuff is what separates it from a plain blob at 18px.
  'MMA': <><path d="M8 11V7.5A3.5 3.5 0 0 1 11.5 4h2A4.5 4.5 0 0 1 18 8.5V13a4 4 0 0 1-4 4h-2.5A3.5 3.5 0 0 1 8 13.5z" {...G} /><path d="M8 12.5H6.5a1.75 1.75 0 0 1 0-3.5H8" {...G} /><path d="M9.5 17v3h7v-3.6" {...G} /></>
}

export const PERSON_GLYPH = <><circle cx="12" cy="8.5" r="3.6" {...G} /><path d="M4.8 20a7.2 7.2 0 0 1 14.4 0" {...G} /></>

export function Glyph({ children, className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">{children}</svg>
  )
}

/**
 * One subject slot. Renders whatever images subjectFor() found, and swaps in a
 * glyph only once EVERY one of them has failed — a match total whose home crest
 * exists but away crest does not still shows the crest it has.
 *
 * Keyed on the urls by the caller, so a re-sort or a page change remounts it and
 * cannot carry a stale failure count onto a different row.
 */
export default function SubjectImage({ opp }) {
  const sport = opp?.sport
  // settlementFields() derives home/away by splitting `match`. A caller that
  // already HAS the two teams (the odds screen groups by fixture, so it never
  // builds a match string) would otherwise have them nulled back out here.
  const fields = settlementFields(opp)
  // *** subject_name: A NAME WE CANNOT TYPE. ***
  // Outright markets (Brownlow Winner, Premiership Winner, tryscorers) send a
  // bare `name` with no player_name/team_name, so the caller cannot say whether
  // it is a person or a club - and guessing from the string is not possible.
  // Rather than guess, BOTH candidate urls are offered: the crest first, the
  // headshot second. The <img> below already walks the list on error, so the
  // one that exists is the one drawn, and a name that is neither falls through
  // to the same glyph it would have got anyway. One wasted 404 on a player row,
  // which the droplet answers from cache.
  const subject = opp?.subject_name
    ? {
        kind: 'unknown',
        urls: [teamLogoUrl(opp.subject_name, sport),
               playerImageUrl(opp.subject_name, sport)].filter(Boolean),
        // Named so the render below can tell which of the two is showing and
        // pick the matching container (disc for a face, bare for a crest).
        headshotUrl: playerImageUrl(opp.subject_name, sport),
        label: opp.subject_name,
        sport,
      }
    : subjectFor({
        ...fields,
        home_team: opp?.home_team ?? fields.home_team,
        away_team: opp?.away_team ?? fields.away_team,
        sport
      })
  const urls = subject?.urls ?? []
  const [failed, setFailed] = useState(0)

  // Reset when the row's images change. Without this, a re-sort or a page turn
  // reuses this component instance and carries the previous row's failure count
  // onto a different bet, hiding an image that would have loaded fine.
  const urlKey = urls.join('|')
  useEffect(() => { setFailed(0) }, [urlKey])

  const noneLeft = urls.length === 0 || failed >= urls.length

  // loading="lazy" matters more here than it looks: the source files are FULL
  // resolution (an AFL headshot is ~210KB, a crest ~80-125KB) and are being
  // painted into a 40px box, so a 50-row page would pull several MB if every
  // row fetched on render. Lazy defers everything below the fold to the scroll,
  // and the droplet serves them with a 30-day cache, so it is a first-visit
  // cost only. decoding="async" keeps the decode off the main thread.
  const imgProps = {
    // NO crossOrigin here, deliberately. These images carry a 30-day cache and
    // were cached long before the droplet started sending
    // Access-Control-Allow-Origin. A CORS-mode request happily reuses one of
    // those stale entries, finds no ACAO on it, and FAILS — which turned every
    // photo on the page into a fallback glyph. Nothing on screen needs CORS;
    // only the share card does, and it asks for it separately.
    loading: 'lazy',
    decoding: 'async',
    onLoad:  e => { e.target.style.opacity = '1' },
    onError: e => { e.target.style.opacity = '0'; setFailed(n => n + 1) }
  }

  if (noneLeft) {
    const group = SPORT_GROUP[sport]
    // Read player_name off the OPPORTUNITY, not off `subject`. subjectFor()
    // returns null outright for a player in a sport with no photo folder, so
    // asking `subject` whether this was a player says "no" and the row falls
    // through to the sport glyph — an NFL player prop drew a football. The bet
    // is still about a person whatever we have on disk, so the person
    // placeholder is the honest fallback either way.
    //
    // A tennis head to head needs no special case here: subjectFor() now types
    // it as 'player', so it lands a person mark like any other single subject. A
    // tennis match total stays a 'pair' and correctly falls to the sport glyph -
    // it is about the match, not about one of the two people in it.
    const isPlayerProp = subject?.kind === 'player' || !!opp?.player_name
    if (isPlayerProp) {
      return (
        <div className="bc-lm-avatar-wrap" title={subject?.label || opp?.player_name || undefined}>
          <Glyph className="bc-ev-glyph bc-ev-glyph-person">{PERSON_GLYPH}</Glyph>
        </div>
      )
    }
    return (
      <div className="bc-lm-logo-wrap" title={subject?.label || undefined}>
        {group && <Glyph className="bc-ev-glyph">{SPORT_GLYPHS[group]}</Glyph>}
      </div>
    )
  }

  // An untyped name, drawn as whichever url survives.
  //
  // *** THE CONTAINER FOLLOWS WHAT ACTUALLY LOADED. *** This file's rule is
  // that a headshot gets the circular crop and a crest sits bare - a crest is
  // already a designed mark, and a circle around it leaves dead space. Here the
  // kind is not known until an image loads, so the wrapper is chosen from WHICH
  // url is current: index 0 is the crest (bare), index 1 the headshot (cropped
  // disc). Without this a player on an outright market rendered square and
  // uncropped while the same player on a vote market rendered as a disc.
  if (subject.kind === 'unknown') {
    const isHeadshot = subject.urls[failed] === subject.headshotUrl
    return isHeadshot ? (
      <div className="bc-lm-avatar-wrap" title={subject.label || undefined}>
        <img key={urls[failed]} src={urls[failed]} alt=""
             className={`bc-lm-avatar${sport === 'NRL' ? ' bc-avatar-body' : ''}`} {...imgProps} />
      </div>
    ) : (
      <div className="bc-lm-logo-wrap" title={subject.label || undefined}>
        <img key={urls[failed]} src={urls[failed]} alt="" className="bc-lm-logo-img" {...imgProps} />
      </div>
    )
  }

  if (subject.kind === 'player') {
    return (
      <div className="bc-lm-avatar-wrap" title={subject.label || undefined}>
        <img src={urls[0]} alt="" className={`bc-lm-avatar${sport === 'NRL' ? ' bc-avatar-body' : ''}`} {...imgProps} />
      </div>
    )
  }

  // A PAIR is drawn bare even in tennis, where both members are faces. Two
  // ringed circles overlapping in a 44px slot read as clutter, and a cutout
  // already carries its own silhouette - the same argument that keeps crests
  // bare. The ring is what marks a SINGLE subject, so it stays on the one-face
  // markets (head to head, handicap, aces) and off the two-face ones (match
  // totals, total games 1st set).
  if (subject.kind === 'pair') {
    return (
      <div className="bc-lm-logo-pair" title={subject.label}>
        {urls.map((url, i) => (
          <div className="bc-lm-logo-wrap" key={i}>
            <img src={url} alt="" className="bc-lm-logo-img" {...imgProps} />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="bc-lm-logo-wrap" title={subject.label || undefined}>
      <img src={urls[0]} alt="" className="bc-lm-logo-img" {...imgProps} />
    </div>
  )
}
